<script setup lang="ts">
import { computed } from 'vue';
import { useConfigStore } from '../store/configStore';

// Left-hand auth brand panel (shared by Login + Signup). No screenshots — a clean
// branded panel whose name/mark come from the settings-configured company_name.
const config = useConfigStore();
const companyName = computed(() => config.getCompanyName);
const brandMark = computed(() => (companyName.value.trim()[0] || '•').toUpperCase());
const year = new Date().getFullYear();

const features = [
  {
    title: 'Order in seconds',
    desc: 'Pin pick-up and drop-off, choose a vehicle, and dispatch — all from one screen.',
  },
  {
    title: 'Live tracking',
    desc: 'Follow every delivery in real time, stop by stop, with driver details.',
  },
  {
    title: 'Complete records',
    desc: 'Search, filter and export every order and invoice to CSV in one click.',
  },
];
</script>

<template>
  <div class="as-panel">
    <div class="as-grid"></div>
    <div class="as-glow"></div>

    <!-- Brand -->
    <div class="as-brand">
      <div class="as-brand-mark">{{ brandMark }}</div>
      <span class="as-brand-name">{{ companyName }}</span>
    </div>

    <!-- Body -->
    <div class="as-body">
      <span class="as-eyebrow">Delivery management</span>
      <h1 class="as-headline">Every delivery,<br />from one place.</h1>
      <p class="as-lede">
        Book, dispatch and track your shipments — and keep a complete, exportable
        record of every order.
      </p>

      <ul class="as-features">
        <li v-for="f in features" :key="f.title" class="as-feature">
          <span class="as-feature-ic">
            <svg v-if="f.title === 'Order in seconds'" viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M13 2 4.5 13.5H11l-1 8.5L19.5 10H13z"/></svg>
            <svg v-else-if="f.title === 'Live tracking'" viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M20 10c0 6-8 11-8 11s-8-5-8-11a8 8 0 0 1 16 0z"/><circle cx="12" cy="10" r="2.6"/></svg>
            <svg v-else viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M8 6h11M8 12h11M8 18h11"/><circle cx="3.5" cy="6" r="1.1"/><circle cx="3.5" cy="12" r="1.1"/><circle cx="3.5" cy="18" r="1.1"/></svg>
          </span>
          <div class="as-feature-txt">
            <span class="as-feature-title">{{ f.title }}</span>
            <span class="as-feature-desc">{{ f.desc }}</span>
          </div>
        </li>
      </ul>
    </div>

    <!-- Footer -->
    <div class="as-footer">© {{ year }} {{ companyName }}</div>
  </div>
</template>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600&family=DM+Serif+Display&display=swap');

.as-panel {
  position: relative;
  width: 58%;
  flex-shrink: 0;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background: #ffffff;
  border-right: 1px solid #edf0f4;
  font-family: 'DM Sans', sans-serif;
}

.as-grid {
  position: absolute; inset: 0; z-index: 0; pointer-events: none;
  background-image:
    linear-gradient(rgba(15,28,46,0.035) 1px, transparent 1px),
    linear-gradient(90deg, rgba(15,28,46,0.035) 1px, transparent 1px);
  background-size: 52px 52px;
  mask-image: radial-gradient(120% 90% at 15% 10%, #000 40%, transparent 100%);
  -webkit-mask-image: radial-gradient(120% 90% at 15% 10%, #000 40%, transparent 100%);
}

.as-glow {
  position: absolute; z-index: 0; pointer-events: none;
  top: -20%; left: -14%; width: 62%; height: 62%;
  background: radial-gradient(circle, rgba(0,97,235,0.07) 0%, transparent 66%);
}

/* Brand */
.as-brand {
  position: relative; z-index: 2;
  display: flex; align-items: center; gap: 12px;
  padding: 40px 56px 0;
}
.as-brand-mark {
  width: 42px; height: 42px; border-radius: 12px;
  background: #0061eb;
  box-shadow: 0 6px 16px rgba(0,97,235,0.26);
  display: flex; align-items: center; justify-content: center;
  font-family: 'DM Serif Display', serif; font-size: 1.25rem; color: #fff;
}
.as-brand-name {
  font-size: 1rem; font-weight: 600;
  color: #0f1c2e; letter-spacing: 0.01em;
}

/* Body */
.as-body {
  position: relative; z-index: 2;
  flex: 1; display: flex; flex-direction: column; justify-content: center;
  padding: 0 56px; max-width: 620px;
}
.as-eyebrow {
  display: inline-block; width: fit-content;
  font-size: 0.7rem; font-weight: 700; letter-spacing: 0.14em;
  text-transform: uppercase; color: #0061eb;
  margin-bottom: 20px;
}
.as-headline {
  font-family: 'DM Serif Display', serif; font-weight: 400;
  font-size: 2.9rem; line-height: 1.1; letter-spacing: -0.02em;
  color: #0f1c2e; margin: 0 0 18px;
}
.as-lede {
  font-size: 1rem; font-weight: 300; line-height: 1.7;
  color: #6b7280; margin: 0 0 44px; max-width: 460px;
}

/* Features */
.as-features { list-style: none; margin: 0; padding: 0; display: flex; flex-direction: column; gap: 22px; }
.as-feature { display: flex; align-items: flex-start; gap: 16px; }
.as-feature-ic {
  flex-shrink: 0; width: 40px; height: 40px; border-radius: 11px;
  background: rgba(0,97,235,0.08); border: 1px solid rgba(0,97,235,0.16);
  display: flex; align-items: center; justify-content: center; color: #0061eb;
}
.as-feature-txt { display: flex; flex-direction: column; gap: 3px; padding-top: 1px; }
.as-feature-title { font-size: 0.95rem; font-weight: 600; color: #1f2937; }
.as-feature-desc { font-size: 0.85rem; font-weight: 300; line-height: 1.55; color: #8a94a6; max-width: 400px; }

/* Footer */
.as-footer {
  position: relative; z-index: 2;
  padding: 0 56px 36px; font-size: 0.78rem;
  color: #9aa1ad; letter-spacing: 0.01em;
}

/* Hide on mobile — the form takes the full screen */
@media (max-width: 900px) { .as-panel { display: none; } }
</style>
